<template>
  <div>
    <content-container>
      <div class="digital-service">
        <transition name="fade" mode="out-in">
          <loader-with-message v-if="dataLoading"
                               :key="'loader'"
                               :has-error="dataFetchError"
                               @retry="fetchPackages"/>
          <div v-else :key="'packages'">
            <transition name="fade" mode="out-in">
              <account-status-blocked v-if="userStatus === 'BLOCKED'" :key="'status-blocked'" :resources-data="$props.resourcesData"/>
              <account-status-on-hold v-else-if="userStatus === 'ON_HOLD'" :key="'status-on-hold'" :resources-data="$props.resourcesData"/>
              <div v-else :key="'status-active'">
                <base-heading>{{ $t(`digitalServices.${serviceName}.pageTitle`) }}</base-heading>
                <h4 class="subtitle">{{ $t(`digitalServices.${serviceName}.title`) }}</h4>
                <div class="description">{{ $t(`digitalServices.${serviceName}.description`) }}</div>
                <div class="cards-container">
                  <digital-service-card v-for="(card, idx) in availablePackages"
                                        :key="'package'+idx"
                                        :description="card.description"
                                        :logo="card.image.url"
                                        :price="card.actualPrice.formattedValue"
                                        :activation-disabled="!card.canActivate && !card.active"
                                        :free-status="(card.freeTrial && !card.active) || card.actualPrice.value <= 0"
                                        :limited-free-label="card.freeTrial"
                                        :old-price="card.originalPrice.formattedValue"
                                        :service-active="card.active"
                                        recurring-price
                                        @open-tooltip="openDescriptionModal(card)"
                                        @activate-service="activateService"
                                        @deactivate-service="deactivateService"/>
                </div>
              </div>
            </transition>
            <purchase-history-table v-if="activatedPackages.length" :activated-packages="activatedPackages" :service="$props.serviceType"/>
          </div>
        </transition>
      </div>
    </content-container>
    <div class="digital-service">
      <!-- info modal -->
      <modal :visible="infoModalVisible"
             @modal-close="infoModalVisible = false"
             size="md"
             class="info-modal"
             :class="infoModalClass">
        <div class="info-modal-body">
          <img v-if="cardData.image" :src="cardData.image.url" :alt="cardData.productName" class="logo">
          <p class="description" v-html="cardData.tooltipDescription"></p>
          <div v-if="cardData.actualPrice" class="actions">
            <div v-if="(cardData.freeTrial && !cardData.active) || cardData.actualPrice.value <= 0" class="free-period">
              <div class="free-period-title">
                <span v-if="cardData.freeTrial">{{ $t('digitalServices.freePeriodTitle') }}</span>
                <div class="free-title">{{ $t('digitalServices.freePeriodSubtitle') }}</div>
              </div>
              <div class="strike-price" v-if="false">
                <div class="inner">{{ cardData.originalPrice.formattedValue }}&nbsp;<span>{{ $t('digitalServices.currency') }}/{{ $t('digitalServices.monthly') }}</span></div>
              </div>
            </div>
            <p v-else class="price">{{ cardData.actualPrice.formattedValue }} <span>{{ $t('digitalServices.currency') }}/{{ $t('digitalServices.monthly') }}</span></p>

            <div class="buttons-container">
              <base-button v-if="cardData.active" variant="secondary" @click="deactivateService">{{ $t('common.deactivate') }}</base-button>
              <base-button v-else-if="(!cardData.freeTrial && cardData.active) || cardData.actualPrice.value > 0" variant="primary" @click="activateService">{{ $t('common.activate') }}</base-button>
              <base-button v-else variant="primary" @click="activateService">{{ $t('digitalServices.activateServiceFree') }}</base-button>
            </div>
          </div>
        </div>
      </modal>

      <!-- confirmation modal -->
      <confirmation-modal :visible="activationModal"
                          :closeButtonVisible="false"
                          @close="activationModal = false"
                          :cancel-button="true"
                          confirm-type="empty"
                          :confirm-title="$t(`digitalServices.${serviceName}.confirmationModal.title`)"
                          :confirm-description="$t(`digitalServices.${serviceName}.confirmationModal.description`)"
                          :confirm-text="$t('common.activate')"
                          :loading="confirmationLoader"
                          @confirm="activateServiceApi"
                          :button-disabled="buttonDisabled"/>

      <!-- deactivation modal -->
      <confirmation-modal :visible="deactivationModal"
                          :closeButtonVisible="false"
                          @close="deactivationModal = false"
                          :cancel-button="true"
                          confirm-type="empty"
                          :confirm-title="$t(`digitalServices.${serviceName}.confirmationModal.deactivateTitle`)"
                          :confirm-description="$t(`digitalServices.${serviceName}.confirmationModal.deactivateDescription`)"
                          :confirm-text="$t('common.deactivate')"
                          :loading="confirmationLoader"
                          @confirm="deactivateServiceApi"
                          :button-disabled="buttonDisabled"/>

      <!-- success modal -->
      <modal :visible="successActivation"
             @modal-close="resetData"
             size="md"
             class="success-modal">
        <h3 class="title">{{ $t(`digitalServices.${serviceName}.successActivationModal.title`) }}</h3>
        <p class="description">{{ $t(`digitalServices.${serviceName}.successActivationModal.description`) }}</p>

        <p v-if="codeType === 'link'" class="code-label">{{ $t(`digitalServices.${serviceName}.successActivationModal.activationLinkLabel`) }}</p>
        <p v-else>{{ $t(`digitalServices.${serviceName}.successActivationModal.activationCodeLabel`) }}</p>

        <a v-if="codeType === 'link'" class="code-link" :href="code" target="_blank" rel="nofollow">{{ code }}</a>
        <v-popover v-else class="digital-tooltip" popover-class="digital-service-popover" trigger="hover" :auto-hide="false" placement="top">
          <button class="code-btn" @click.stop.prevent="copyCode(code)">
            <span class="code">{{ code }}</span>
          </button>
          <template slot="popover">
            <div class="digital-tooltip-description">
              <div class="tooltip-content-wrapper">
                <transition name="fade" mode="out-in">
                  <img v-if="codeCopied" src="/assets/images/green_circle_checkmark.svg" alt="" class="digital-success-icon"/>
                </transition>
                {{ codeCopied ? $t('digitalServices.tooltipSuccessCodeCopy') : $t('digitalServices.tooltipCopyCodeText') }}
              </div>
            </div>
          </template>
        </v-popover>

        <div class="app-links">
          <a :href="$t(`digitalServices.${serviceName}.appLinks.appStore`)" target="_blank" rel="nofollow">
            <img class="store-button" src="/assets/images/app-store-badge.png" alt="app-store">
          </a>
          <a :href="$t(`digitalServices.${serviceName}.appLinks.googlePlay`)" target="_blank" rel="nofollow">
            <img class="store-button" src="/assets/images/google-play-badge.png" alt="google-play">
          </a>
          <a v-if="$props.serviceType === 'HBO' || $props.serviceType === 'DEEZER'" :href="checkOnWebUrl" target="_blank" rel="nofollow">
            <img class="store-button" src="/assets/images/check-on-web.png" alt="check-on-web">
          </a>
        </div>
      </modal>
    </div>
    <digital-instruction-sticky-button :service-name="$props.serviceType" />
  </div>
</template>

<script>
import DigitalServiceCard from '@/components/digital-services/digital-service-card'
import AccountStatusBlocked from '@/components/services/account-status/account-status-blocked'
import selectionBlockEvents from '@/mixins/selectionBlockEvents'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import ConfirmationModal from '@/components/common/modal/confirmation-modal'
import LoaderWithMessage from '@/components/common/loader-with-message'
import AccountStatusOnHold from '@/components/services/account-status/account-status-on-hold'
import PurchaseHistoryTable from '@/components/common/purchase-history/purchase-history-table'
import Modal from '@/components/common/modal/modal'
import { copyText } from '@/utils/copyTextUtil'
import ContentContainer from '@/components/common/main-layout/content-container'
import DigitalInstructionStickyButton from '@/components/digital-services/digital-instruction-sticky-button'

export default {
  name: 'digital-service-common',
  components: { DigitalInstructionStickyButton, ContentContainer, Modal, PurchaseHistoryTable, AccountStatusOnHold, LoaderWithMessage, AccountStatusBlocked, DigitalServiceCard, ConfirmationModal },
  props: {
    resourcesData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    serviceType: {
      type: String,
      default: ''
    }
  },
  mixins: [selectionBlockEvents, errorMessageHandler],
  computed: {
    serviceName () {
      return this.$props.serviceType.toLowerCase()
    },
    checkOnWebUrl () {
      return this.serviceName === 'hbo' ? 'https://hbomax.com' : 'https://www.deezer.com/en/'
    },
    infoModalClass () {
      return {
        [`info-bg-${this.serviceName}`]: this.serviceName
      }
    }
  },
  data () {
    return {
      dataLoading: true,
      dataFetchError: false,
      buttonDisabled: false,
      availablePackages: [],
      activatedPackages: [],
      userNotEligible: false,
      userStatus: '',
      activationModal: false,
      deactivationModal: false,
      successActivation: false,
      code: '',
      codeCopied: false,
      infoModalVisible: false,
      cardData: {},
      codeType: 'code',
      confirmationLoader: false
    }
  },
  methods: {
    fetchPackages () {
      if (this.$props.resourcesData.subscriptionInfo && this.$props.resourcesData.subscriptionInfo.customerId) {
        this.dataLoading = true
        this.dataFetchError = false
        this.blockSidebarSelection()
        const customerId = this.$props.resourcesData.subscriptionInfo.customerId

        this.$apiRequest.get(`user/${this.serviceName}?customerId=${customerId}`)
          .then(response => {
            const availablePackages = response.data.available || []
            const activatedPackages = response.data.activated || []

            this.availablePackages = this.getAsArray(availablePackages)
            this.activatedPackages = this.getAsArray(activatedPackages)

            this.userStatus = response.data.contractStatus
            this.dataLoading = false
          })
          .catch(e => {
            this.dataFetchError = true
          })
          .finally(() => {
            this.unblockSidebarSelection()
          })
      }
    },
    getAsArray (data) {
      if (!data) return []
      if (Array.isArray(data)) return data
      return [data]
    },
    activateService () {
      this.infoModalVisible = false
      this.activationModal = true
    },
    activateServiceApi () {
      this.buttonDisabled = true
      this.confirmationLoader = true
      const id = this.$props.resourcesData.subscriptionInfo.customerId
      const dataToSend = {
        activate: true,
        customerId: id
      }

      this.$apiRequest.post(`user/${this.serviceName}`, dataToSend)
        .then(response => {
          if (response.status === 200) {
            this.activationModal = false
            this.successActivation = true
            if (response.data.activationLink) {
              this.codeType = 'link'
              this.code = response.data.activationLink
            } else if (response.data.activationCode) {
              this.codeType = 'code'
              this.code = response.data.activationCode
            } else {
              this.codeType = 'code'
              this.code = Object.values(response.data)[0]
            }
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.buttonDisabled = false
          this.confirmationLoader = false
        })
    },
    deactivateService () {
      this.infoModalVisible = false
      this.deactivationModal = true
    },
    deactivateServiceApi () {
      this.infoModalVisible = false
      this.buttonDisabled = true
      this.confirmationLoader = true
      const id = this.$props.resourcesData.subscriptionInfo.customerId
      const dataToSend = {
        activate: false,
        customerId: id
      }
      this.$apiRequest.post(`user/${this.serviceName}`, dataToSend)
        .then(response => {
          if (response.status === 200) {
            this.deactivationModal = false
            this.fetchPackages()
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t(`digitalServices.${this.serviceName}.deactivationModal.successDeactivationMessage`)
            })
          }
          this.closeModalAndDelayFetch()
        })
        .catch(e => {
          this.dataFetchError = true
        })
        .finally(() => {
          this.buttonDisabled = false
          this.confirmationLoader = false
        })
    },
    resetData () {
      this.successActivation = false
      this.infoModalVisible = false
      this.codeCopied = false
      this.fetchPackages()
    },
    openDescriptionModal (cardData) {
      this.infoModalVisible = true
      this.cardData = cardData
    },
    copyCode (code) {
      copyText(code)
        .then(() => {
          this.codeCopied = true
        })
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.fetchPackages()
    })
  }
}
</script>

<style scoped lang="scss" src="./styles/digital-service-page.scss"></style>
